import {
  Accordion,
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontFamily,
  FontWeight,
  Icon,
  IconVariant,
  JustifyContent,
  Space,
  Text,
  ZIndex,
} from '@gousto-internal/citrus-react'
import { queueTrackingEvent } from '@library/tracking-integration'
import React from 'react'
import { CuratedBoxImageLayout } from '../CuratedBoxImageLayout/CuratedBoxImageLayout'
import { CuratedBoxRecipeProps, CuratedBoxRecipeTileProps } from '../../types/curatedBoxTypes'
import { CuratedBoxRecipePreviewLineItem, IconButton, RecipeTagRow } from './components'
import { CuratedBoxButton } from '../CuratedBoxButton/CuratedBoxButton'

const CuratedBoxRecipeTile = ({
  handleOnClick,
  title,
  tags,
  topInStockRecipes,
  curatedBoxCollectionId,
  isExpanded,
  numPortions,
}: CuratedBoxRecipeTileProps) => {
  const curatedBoxImgUrls = topInStockRecipes.map((item) => item?.recipeImageSrc)

  if (isExpanded) {
    const trackingData = {
      action: 'click_curated_box_expand',
      properties: {
        component: 'curated_box_expand',
        boxName: title,
      },
    }
    queueTrackingEvent<typeof trackingData>(trackingData)
  }

  const handleEvent = () => {
    const recipeIds = topInStockRecipes.map((recipe) => recipe?.recipeId) as string[]
    handleOnClick(recipeIds, curatedBoxCollectionId, title)
  }
  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    event.stopPropagation()
    if (event.key === 'Enter') {
      handleEvent()
    }
  }

  const onClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    handleEvent()
  }

  return (
    <Box data-testid="curatedBoxRecipeTile">
      <Box position={DisplayPosition.Relative}>
        <CuratedBoxImageLayout
          numPortions={numPortions}
          curatedBoxImgURLS={curatedBoxImgUrls as string[]}
        />
        <Box
          position={DisplayPosition.Absolute}
          left="4px"
          bottom="-24px"
          right="4px"
          display={Display.Flex}
          alignItems={AlignItems.Center}
          flexDirection={FlexDirection.Row}
          justifyContent={JustifyContent.SpaceBetween}
        >
          <Box>
            <RecipeTagRow recipeTags={tags} />
          </Box>
          <Box paddingRight={2}>
            <IconButton handleOnKeyDown={handleOnKeyDown} handleClick={onClick} />
          </Box>
        </Box>
      </Box>
      <Box paddingH={3}>
        <Box paddingV={2}>
          <Space size={3} />
          <Text size={3} fontFamily={FontFamily.Bold} fontWeight={FontWeight.SemiBold}>
            {title}
          </Text>
        </Box>
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Row}
        justifyContent={JustifyContent.FlexEnd}
        paddingH={3}
        paddingBottom={2}
      >
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Row}
          alignItems={AlignItems.Center}
          paddingBottom={1}
        >
          <Box>
            <Text color={Color.Secondary_400} fontFamily={FontFamily.Bold} size={2}>
              What&apos;s in the box?{' '}
            </Text>
          </Box>
          <Box>
            <Space size={1} />
            {isExpanded ? (
              <Icon variant={IconVariant.Actionable} name="chevron_up" />
            ) : (
              <Icon variant={IconVariant.Actionable} name="chevron_down" />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const CuratedBoxRecipe = ({
  curatedBoxData,
  handleOnClick,
  numPortions,
  handleRecipePreview,
  handleEditCuratedBox,
}: CuratedBoxRecipeProps) => {
  const { topInStockRecipes, tags, title, curatedBoxCollectionId } = curatedBoxData

  return (
    <Box
      boxShadow="0px 4px 20px 0px rgba(0, 0, 0, 0.15);"
      borderRadius={1}
      position={[null, null, DisplayPosition.Relative]}
      bg={Color.White}
    >
      <Accordion
        id={`curatedBoxRecipePreviewAccordionButton-${title}`}
        title={(isExpanded) => (
          <CuratedBoxRecipeTile
            title={title}
            curatedBoxCollectionId={curatedBoxCollectionId}
            handleOnClick={handleOnClick}
            tags={tags}
            topInStockRecipes={topInStockRecipes}
            isExpanded={isExpanded}
            numPortions={numPortions}
          />
        )}
      >
        <Box
          bg={Color.White}
          zIndex={[null, null, ZIndex.Mask]}
          left={[null, null, '0px']}
          right={[null, null, '0px']}
          paddingBottom={2}
          position={[null, null, DisplayPosition.Absolute]}
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
        >
          {topInStockRecipes.map((topInStockRecipe) => (
            <CuratedBoxRecipePreviewLineItem
              key={topInStockRecipe.recipeId}
              title={topInStockRecipe.title}
              prepTimes={topInStockRecipe.prepTimes}
              recipeImageSrc={topInStockRecipe.recipeImageSrc}
              recipeId={topInStockRecipe.recipeId}
              handleRecipe={handleRecipePreview}
              curatedBoxCollectionId={curatedBoxCollectionId}
            />
          ))}
          <Box paddingH={4}>
            <CuratedBoxButton
              handleRecipes={handleOnClick}
              recipes={topInStockRecipes}
              curatedBoxCollectionId={curatedBoxCollectionId}
              title={title}
              isPrimary
              buttonText="Add box"
            />
            <Space size={2} />
            <CuratedBoxButton
              handleRecipes={handleEditCuratedBox}
              recipes={topInStockRecipes}
              curatedBoxCollectionId={curatedBoxCollectionId}
              title={title}
              isPrimary={false}
              buttonText="Edit box"
            />
          </Box>
          <Space size={3} />
        </Box>
      </Accordion>
    </Box>
  )
}
